import React, { useEffect, useState, useCallback } from 'react';
import SquareIcon from '../../../assets/square.svg';
import {
  Spin,
  message,
  Select,
  Row,
  Col,
  Tag,
  Tooltip,
  Modal,
  Alert,
  Dropdown,
} from 'antd';
import { TWButton } from '../../../components/ui';
import { TableRowsSkeleton } from '../../../components/skeleton/skeleton';
import {
  CloseCircleOutlined,
  CheckCircleFilled,
  MoreOutlined,
} from '@ant-design/icons';
import storeApi from '../../../services/storeApi';
import oauthApi from '../../../services/oauthApi';
import { OAuthProviderType } from '../../../data/enums';
import ModalConfirmation from '../components/ModalConfirmation';
import SquareMenuTools from './SquareMenuTools';
import useSquareConnectInfo from '../../../components/menu/square-shared/use-square-connect-info';

const { Option } = Select;

const SquareIntegration = ({ globalCompany }) => {
  const [loading, setLoading] = useState({
    storeLoading: false,
    squareLocationLoading: false,
    integrations: false,
  });
  const [storeList, setStoreList] = useState([]);
  const [squareLocationList, setSquareLocationList] = useState([]);
  const [selectedPairings, setSelectedPairings] = useState({});

  const [companyConnection, setCompanyConnection] = useState(null);
  const [checkingConnection, setCheckingConnection] = useState(false);

  const getSquareLocationList = useCallback(async () => {
    setLoading((prev) => ({ ...prev, squareLocationLoading: true }));
    try {
      const query = {
        CompanyId: globalCompany.id,
        Type: OAuthProviderType.Square,
      };
      const list = await oauthApi.getLocationList(query);
      if (list.data && list.data.length > 0) {
        setSquareLocationList(list.data);
      } else {
        setSquareLocationList([]);
      }
    } catch (error) {
      setSquareLocationList([]);
    } finally {
      setLoading((prev) => ({ ...prev, squareLocationLoading: false }));
    }
  }, [globalCompany.id]);

  const getStoreList = useCallback(async () => {
    setLoading((prev) => ({ ...prev, storeLoading: true }));
    try {
      const query = `companyid=${globalCompany.id}`;
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) {
        setStoreList(list.data.data);
      }
    } catch (error) {
      console.error('Error fetching store list', error);
    }
    setLoading((prev) => ({ ...prev, storeLoading: false }));
  }, [globalCompany.id]);

  const checkSquareConnection = useCallback(async () => {
    if (!globalCompany?.id) return;
    setCheckingConnection(true);
    setCompanyConnection(null);
    try {
      const type = OAuthProviderType.Square;
      const response = await oauthApi.checkOAuth(
        globalCompany.id,
        `?type=${type}`
      );
      const isConnected = Boolean(response.data.data);
      setCompanyConnection(isConnected);
      if (isConnected) {
        getSquareLocationList();
      } else {
        setSquareLocationList([]);
      }
    } catch (error) {
      console.error('Body: Error checking Square connection', error);
      setCompanyConnection(false);
      setSquareLocationList([]);
    } finally {
      setCheckingConnection(false);
    }
  }, [globalCompany?.id, getSquareLocationList]);

  const initializeSelectedPairings = useCallback((stores, locations) => {
    const initialPairings = {};
    stores.forEach((store) => {
      const integratedLocation = locations.find(
        (location) => location.storeId === store.id && location.isIntegrated
      );
      if (integratedLocation) {
        initialPairings[store.id] = integratedLocation.id;
      }
    });
    setSelectedPairings(initialPairings);
  }, []);

  useEffect(() => {
    const refreshBodyData = async () => {
      setLoading((prev) => ({
        ...prev,
        storeLoading: true,
        squareLocationLoading: true,
      }));
      try {
        await getStoreList();
        await checkSquareConnection();
      } catch (error) {
        console.error('Error refreshing body data', error);
      } finally {
        setLoading((prev) => ({
          ...prev,
          storeLoading: false,
          squareLocationLoading: false,
        }));
      }
    };
    refreshBodyData();
  }, [getStoreList, checkSquareConnection]);

  useEffect(() => {
    if (storeList.length > 0 && squareLocationList.length > 0) {
      initializeSelectedPairings(storeList, squareLocationList);
    }
  }, [storeList, squareLocationList, initializeSelectedPairings]);

  const handleLocationSelect = (storeId, locationId) => {
    setSelectedPairings((prev) => ({
      ...prev,
      [storeId]: locationId,
    }));
  };

  const getLocationNameById = (locationId) => {
    const location = squareLocationList.find((loc) => loc.id === locationId);
    return location ? location.name : 'Unknown location';
  };

  const getIntegratedLocationId = (storeId) => {
    const location = squareLocationList.find(
      (loc) => loc.storeId === storeId && loc.isIntegrated
    );
    return location ? location.id : null;
  };

  const integrateStoreLocation = async (
    storeId,
    locationId,
    isIntegrated = true
  ) => {
    if (!locationId && isIntegrated) {
      message.error('Please select a Square location first');
      return;
    }
    setLoading((prev) => ({ ...prev, integrations: true }));
    try {
      const body = {
        companyId: globalCompany.id,
        storeId,
        locationId,
        type: OAuthProviderType.Square,
        isIntegrated,
      };
      await oauthApi.postIntegrationStoreLocation(body);
      if (isIntegrated) {
        message.success('Store integrated with Square location successfully');
      } else {
        message.success('Integration removed successfully');
      }
      await checkSquareConnection();
      await getStoreList();
    } catch (error) {
      console.error('Error with integration', error);
      message.error(
        `Failed to ${isIntegrated ? 'Integrate' : 'Remove Integration'}: ` +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setLoading((prev) => ({ ...prev, integrations: false }));
    }
  };

  const removeIntegration = async (storeId, locationId) => {
    try {
      await integrateStoreLocation(storeId, locationId, false);
    } catch (error) {
      console.error('Error removing integration', error);
      message.error('Failed to remove integration.');
    }
  };

  const promptRemoveIntegration = (storeId, locationId) => {
    Modal.confirm({
      title: 'Unlink this store from Square?',
      content:
        'This store will stop syncing with Square — menu import/sync, price and stock updates. The Square account stays connected; you can re-link anytime.',
      okText: 'Unlink',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => removeIntegration(storeId, locationId),
    });
  };

  return (
    <>
      {checkingConnection ? (
        <div className="pl-5 py-4 w-full">
          <TableRowsSkeleton count={3} rowHeight="h-12" />
        </div>
      ) : companyConnection === false ? (
        <div className="pl-5 py-4 text-sm text-gray-500 dark:text-slate-400">
          Connect to Square using the button in the header to manage
          integrations.
        </div>
      ) : companyConnection === true ? (
        <div className="pl-5 pr-5 pt-4">
          <div className="mb-4">
            <h3 className="text-md font-medium text-gray-700 dark:text-slate-300">
              Integrate Stores with Square Locations
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Select a Square location for each of your stores and click
              Integrate
            </p>
          </div>

          {loading.storeLoading || loading.squareLocationLoading ? (
            <div className="w-full py-4">
              <TableRowsSkeleton count={4} rowHeight="h-16" />
            </div>
          ) : (
            <div className="space-y-4">
              {storeList.length > 0 && squareLocationList.length > 0 ? (
                storeList.map((store) => {
                  const integratedLocationId = getIntegratedLocationId(
                    store.id
                  );
                  return (
                    <div
                      key={store.id}
                      className="flex flex-col md:flex-row justify-between items-start md:items-center py-3 border-b last:border-0"
                    >
                      <div className="flex flex-col mb-2 md:mb-0 md:w-1/3">
                        <span className="text-sm text-gray-700 dark:text-slate-300">
                          {store.name}
                        </span>
                        {integratedLocationId && (
                          <Tag color="blue" className="mt-1 w-fit">
                            Connected:{' '}
                            {getLocationNameById(integratedLocationId)}
                          </Tag>
                        )}
                      </div>

                      <Row
                        gutter={[8, 8]}
                        className="w-full md:w-2/3"
                        align="middle"
                        justify="end"
                      >
                        <Col xs={24} sm={16} md={16} lg={14} xl={12}>
                          <div
                            className="flex items-center w-full"
                            style={{ height: '32px' }}
                          >
                            {integratedLocationId && (
                              <Tooltip title="Unlink this store from its Square location">
                                <TWButton
                                  variant="danger"
                                  size="sm"
                                  icon={CloseCircleOutlined}
                                  onClick={() =>
                                    promptRemoveIntegration(
                                      store.id,
                                      integratedLocationId
                                    )
                                  }
                                  className="mr-2 flex-shrink-0"
                                />
                              </Tooltip>
                            )}
                            <Select
                              placeholder="Select Square Location"
                              className="flex-grow min-w-0"
                              style={{ height: '32px' }}
                              onChange={(value) =>
                                handleLocationSelect(store.id, value)
                              }
                              value={
                                integratedLocationId ||
                                selectedPairings[store.id] ||
                                undefined
                              }
                              disabled={!!integratedLocationId}
                            >
                              {squareLocationList.map((location) => {
                                const integratedWithAnotherStore =
                                  location.isIntegrated &&
                                  location.storeId !== store.id;
                                return (
                                  <Option
                                    key={location.id}
                                    value={location.id}
                                    disabled={integratedWithAnotherStore}
                                    title={
                                      integratedWithAnotherStore
                                        ? `Connected to ${
                                            storeList.find(
                                              (s) => s.id === location.storeId
                                            )?.name || 'another store'
                                          }`
                                        : location.name
                                    }
                                  >
                                    {location.name}
                                    {integratedWithAnotherStore && (
                                      <span className="text-xs text-gray-400 ml-1">
                                        (Connected:{' '}
                                        {storeList.find(
                                          (s) => s.id === location.storeId
                                        )?.name || 'other'}
                                        )
                                      </span>
                                    )}
                                  </Option>
                                );
                              })}
                            </Select>
                          </div>
                        </Col>

                        <Col xs={24} sm={8} md={8} lg={6} xl={4}>
                          <TWButton
                            variant="primary"
                            label="Integrate"
                            onClick={() =>
                              integrateStoreLocation(
                                store.id,
                                selectedPairings[store.id],
                                true
                              )
                            }
                            disabled={
                              !!integratedLocationId ||
                              !selectedPairings[store.id]
                            }
                            className="w-full"
                          />
                        </Col>
                      </Row>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-gray-500 dark:text-slate-400 py-2">
                  {storeList.length === 0
                    ? 'No This Way stores found.'
                    : squareLocationList.length === 0
                    ? 'No available Square locations found.'
                    : 'Loading data...'}
                </div>
              )}
            </div>
          )}

          <SquareMenuTools
            globalCompany={globalCompany}
            storeList={storeList}
            squareLocationList={squareLocationList}
          />
        </div>
      ) : null}
    </>
  );
};

export const SquareIntegrationHeader = ({ globalCompany }) => {
  const [companyConnection, setCompanyConnection] = useState(null);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [isDisconnectModalVisible, setIsDisconnectModalVisible] =
    useState(false);
  const { scopes: apiScopes, needsReconnect } = useSquareConnectInfo();

  const checkSquareConnection = useCallback(async () => {
    if (!globalCompany?.id) return;
    setCheckingConnection(true);
    try {
      const type = OAuthProviderType.Square;
      const response = await oauthApi.checkOAuth(
        globalCompany.id,
        `?type=${type}`
      );
      setCompanyConnection(Boolean(response.data.data));
    } catch (error) {
      console.error('Header: Error checking Square connection', error);
      setCompanyConnection(false);
    } finally {
      setCheckingConnection(false);
    }
  }, [globalCompany?.id]);

  useEffect(() => {
    checkSquareConnection();
  }, [checkSquareConnection]);

  // Fallback only — the live list comes from OAuth/SquareConnectInfo (api is the source of truth). Keep in sync with SquareOAuthScopes.Required.
  const squareScopes = [
    'MERCHANT_PROFILE_READ',
    'ITEMS_READ',
    'ORDERS_READ',
    'ORDERS_WRITE',
    'PAYMENTS_WRITE',
    'CUSTOMERS_WRITE',
    'INVENTORY_READ',
  ];

  const connectSquare = () => {
    const url = `${
      process.env.REACT_APP_SQUARE_BASE_URL
    }/oauth2/authorize?client_id=${
      process.env.REACT_APP_SQUARE_CLIENT_ID
    }&response_type=code&scope=${(apiScopes && apiScopes.length
      ? apiScopes
      : squareScopes
    ).join('+')}&state=${
      globalCompany.id
    }&redirect_uri=${process.env.REACT_APP_SQUARE_REDIRECT_URI}`;
    window.location.href = url;
  };

  const promptDisconnectSquare = () => {
    setIsDisconnectModalVisible(true);
  };

  const handleDisconnectConfirm = async () => {
    setIsDisconnectModalVisible(false);
    setCheckingConnection(true);
    try {
      const body = {
        companyId: globalCompany.id,
        type: OAuthProviderType.Square,
      };
      await oauthApi.disconnectOAuth(body);
      message.success('Square disconnected successfully');
      setCompanyConnection(false);
    } catch (error) {
      message.error(
        'Failed to disconnect Square ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setCheckingConnection(false);
    }
  };

  return (
    <>
      {needsReconnect && (
        <div onClick={(e) => e.stopPropagation()}>
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message="Square connection needs to be renewed — new permissions are required. Menu, price and stock sync may be paused."
            action={
              <TWButton onClick={connectSquare}>Reconnect with Square</TWButton>
            }
          />
        </div>
      )}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <img className="pl-5" src={SquareIcon} alt="Square" />
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-slate-400">
              Square
            </div>
          </div>
        </div>
        <div className="pr-5">
          {checkingConnection ? (
            <Spin />
          ) : companyConnection !== null ? (
            companyConnection ? (
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <CheckCircleFilled
                  style={{ fontSize: '32px', color: '#52c41a' }}
                />
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'disconnect',
                        danger: true,
                        label: 'Disconnect Square account',
                      },
                    ],
                    onClick: ({ key }) => {
                      if (key === 'disconnect') promptDisconnectSquare();
                    },
                  }}
                >
                  <button
                    type="button"
                    className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-600"
                  >
                    <MoreOutlined style={{ fontSize: '20px' }} />
                  </button>
                </Dropdown>
              </div>
            ) : (
              <TWButton
                variant="primary"
                label="Connect"
                onClick={(e) => {
                  e.stopPropagation();
                  connectSquare();
                }}
              />
            )
          ) : null}
        </div>
      </div>
      <ModalConfirmation
        show={isDisconnectModalVisible}
        onClose={() => setIsDisconnectModalVisible(false)}
        onSubmit={handleDisconnectConfirm}
        title="Confirm Disconnection"
        description="Are you sure you want to disconnect your Square account? This will remove all existing integrations."
        buttonText="Disconnect"
        cancelText="Cancel"
      />
    </>
  );
};

export default SquareIntegration;
